import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

interface User {
  _id: string;
  nickname: string;
  username: string;
  role: string;
  project: any;
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  hasRole: (...roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("dataset_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.auth.me();
      setUser(res.data);
    } catch {
      localStorage.removeItem("dataset_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (username: string, password: string) => {
    const res = await api.auth.login({ username, password });
    localStorage.setItem("dataset_token", res.data.token);
    setUser(res.data.user);
    navigate("/");
  };

  const logout = () => {
    localStorage.removeItem("dataset_token");
    setUser(null);
    navigate("/login");
  };

  const hasRole = (...roles: string[]) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
